<script setup>
/* eslint-env browser */
/* global FB */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import {
  ensureFacebookSdkReady,
  primeFacebookSdk,
} from 'dashboard/helper/facebookSdk';
import { buildInstagramFacebookLoginOptions } from 'dashboard/helper/instagramFacebookLoginOptions';
import instagramClient from 'dashboard/api/channel/instagramClient';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PageHeader from '../../../SettingsSubPageHeader.vue';
import * as Sentry from '@sentry/vue';

const props = defineProps({
  channelType: {
    type: String,
    default: 'direct',
  },
});

const emit = defineEmits(['back', 'created']);

const { t } = useI18n();
const { accountId } = useAccount();
const { replaceInstallationName } = useBranding();
const globalConfig = useMapGetter('globalConfig/get');

const isCommentsChannel = computed(() => props.channelType === 'comments');

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const hasLoginStarted = ref(false);
const isCreating = ref(false);
const userAccessToken = ref('');
const pageList = ref([]);
const selectedPage = ref({ name: null, id: null, access_token: null });
const pageName = ref('');
const emptyStateMessage = ref('');

const i18nPrefix = computed(() =>
  isCommentsChannel.value ? 'INSTAGRAM_COMMENTS' : 'INSTAGRAM'
);

const rules = {
  pageName: { required },
  selectedPage: {
    isValid() {
      return selectedPage.value?.id != null;
    },
  },
};

const v$ = useVuelidate(rules, { pageName, selectedPage });

const showLoader = computed(() => !userAccessToken.value || isCreating.value);

const selectablePages = computed(() =>
  pageList.value.filter(item => !item.exists)
);

function instagramAccountLabel(page) {
  if (page.instagram_username) {
    return `@${page.instagram_username}`;
  }

  if (page.instagram_name) {
    return page.instagram_name;
  }

  return page.name;
}

function defaultInboxName(page) {
  if (page.instagram_username) {
    return `@${page.instagram_username}`;
  }

  return page.instagram_name || page.name;
}

const comboBoxPageOptions = computed(() =>
  selectablePages.value.map(page => ({
    value: page.id,
    label: instagramAccountLabel(page),
  }))
);

const facebookLoginOptions = computed(() =>
  buildInstagramFacebookLoginOptions(globalConfig.value, {
    isCommentsChannel: isCommentsChannel.value,
  })
);

onMounted(() => {
  primeFacebookSdk();
});

async function startLogin() {
  hasLoginStarted.value = true;
  emptyStateMessage.value = t('INBOX_MGMT.DETAILS.LOADING_FB');

  try {
    await ensureFacebookSdkReady();
    tryFBlogin();
  } catch (error) {
    if (error.name === 'ScriptLoaderError') {
      useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
    } else {
      Sentry.captureException(error);
      useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
    }
  }
}

function tryFBlogin() {
  FB.login(response => {
    hasError.value = false;
    if (response.status === 'connected') {
      fetchPages(response.authResponse.accessToken);
    } else if (response.status === 'not_authorized') {
      hasError.value = true;
      errorStateMessage.value = t('INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED');
      errorStateDescription.value = t(
        'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
      );
    } else {
      hasError.value = true;
      errorStateMessage.value = t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
      errorStateDescription.value = '';
    }
  }, facebookLoginOptions.value);
}

async function fetchPages(token) {
  try {
    const response = await instagramClient.fetchFacebookPages({
      omniauthToken: token,
      channelType: props.channelType,
    });
    const {
      data: {
        data: { page_details: pages, user_access_token: accessToken },
      },
    } = response;

    pageList.value = pages || [];
    userAccessToken.value = accessToken;

    if (!selectablePages.value.length) {
      hasError.value = true;
      if (pageList.value.length > 0) {
        errorStateMessage.value = t(
          `INBOX_MGMT.ADD.${i18nPrefix.value}.ALREADY_CONNECTED`
        );
      } else {
        errorStateMessage.value = t(
          `INBOX_MGMT.ADD.${i18nPrefix.value}.NO_IG_PAGES`
        );
        errorStateDescription.value = t(
          `INBOX_MGMT.ADD.${i18nPrefix.value}.NO_IG_PAGES_HELP`
        );
      }
    }
  } catch (error) {
    hasError.value = true;
    if (error.response?.status === 422) {
      errorStateMessage.value = t('INBOX_MGMT.ADD.INSTAGRAM.FB_CONFIG_ERROR');
      errorStateDescription.value = t(
        'INBOX_MGMT.ADD.INSTAGRAM.FB_CONFIG_ERROR_HELP'
      );
    } else {
      errorStateMessage.value = t(
        `INBOX_MGMT.ADD.${i18nPrefix.value}.ERROR_AUTH`
      );
    }
  }
}

function setPageName(pageId) {
  const page = pageList.value.find(p => p.id === pageId);
  if (page) {
    selectedPage.value = page;
    pageName.value = defaultInboxName(page);
  } else {
    selectedPage.value = { name: null, id: null, access_token: null };
    pageName.value = '';
  }
  v$.value.selectedPage.$touch();
}

async function createChannel() {
  v$.value.$touch();
  if (v$.value.$error) return;

  isCreating.value = true;
  emptyStateMessage.value = t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');

  try {
    const response = await instagramClient.registerFacebookPage({
      user_access_token: userAccessToken.value,
      page_access_token: selectedPage.value.access_token,
      page_id: selectedPage.value.id,
      inbox_name: pageName.value?.trim(),
      channel_type: props.channelType,
    });

    emit('created', response.data);
  } catch (error) {
    useAlert(t(`INBOX_MGMT.ADD.${i18nPrefix.value}.ERROR_AUTH`));
    isCreating.value = false;
  }
}
</script>

<template>
  <div class="w-full">
    <div v-if="!hasLoginStarted" class="flex flex-col items-center text-center">
      <button
        type="button"
        class="flex max-w-lg items-center"
        @click="startLogin"
      >
        <img
          class="w-auto h-10 rounded-md"
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook-logo"
        />
      </button>
      <p class="py-6 text-sm text-n-slate-11">
        {{ $t(`INBOX_MGMT.ADD.${i18nPrefix}.HELP_FACEBOOK`) }}
      </p>
      <Button
        link
        slate
        :label="$t('INBOX_MGMT.ADD.INSTAGRAM.BACK_TO_AUTH_OPTIONS')"
        @click="emit('back')"
      />
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
        <Button
          link
          slate
          class="mt-4"
          :label="$t('INBOX_MGMT.ADD.INSTAGRAM.BACK_TO_AUTH_OPTIONS')"
          @click="emit('back')"
        />
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <form
        v-else
        class="flex flex-col flex-wrap mx-0 text-left"
        @submit.prevent="createChannel"
      >
        <PageHeader
          :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')"
          :header-content="
            replaceInstallationName(
              $t(`INBOX_MGMT.ADD.${i18nPrefix}.DETAILS_DESC`)
            )
          "
        />
        <div class="w-full mb-2">
          <span class="text-n-slate-12">
            {{ $t(`INBOX_MGMT.ADD.${i18nPrefix}.CHOOSE_ACCOUNT`) }}
          </span>
          <ComboBox
            :model-value="selectedPage.id"
            :options="comboBoxPageOptions"
            :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
            :has-error="v$.selectedPage.$error"
            class="[&>div>button]:!bg-n-alpha-black2 mt-1"
            @update:model-value="setPageName"
          />
        </div>
        <div class="w-full mb-4">
          <label :class="{ error: v$.pageName.$error }">
            {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
            <input
              v-model="pageName"
              type="text"
              class="mt-1"
              :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
              @input="v$.pageName.$touch"
            />
          </label>
        </div>
        <div class="flex items-center justify-between gap-3">
          <Button
            link
            slate
            :label="$t('INBOX_MGMT.ADD.INSTAGRAM.BACK_TO_AUTH_OPTIONS')"
            @click="emit('back')"
          />
          <Button
            type="submit"
            :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')"
            :is-loading="isCreating"
          />
        </div>
      </form>
    </div>
  </div>
</template>
